/**
 * WorkOrderTab (JOB-PACKET-01 + SUB-SEND-01) — the paper packet reborn as a
 * projection of the job record. The old flow: fill the Excel, print the tabs,
 * scan the cover sheet, email Powerhouse. The new flow: the SCOPE assembles
 * itself from what's already on the job (equipment, visits, permits, access
 * notes), the warehouse strip mirrors pull status, and "Send to subcontractor"
 * emails the scope (no pricing) with a receipt stamped on the job.
 *
 * Subcontractors are a real editable directory (pegasus_subcontractor) —
 * change Powerhouse's email, add a plumber or crane company, deactivate one —
 * managed right here (Seth 2026-08-18: "What if our subcontractor changes?").
 * Sends are comms-gated like every other outbound: HELD until comms_live goes
 * on at rollout, so nothing reaches a real sub during testing.
 */
import { Fragment, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Send, Package, KeyRound, Users, Plus, Check, X,
  Pencil, AlertCircle, RotateCcw, Wrench, FileText, CalendarClock, ArrowRight,
  MapPin, Printer, StickyNote, Eye,
} from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { useMemberName } from "@/components/TeamMemberSelect";
import { useCustomerSignals } from "@/features/customer-360/api";
import { useJobEquipment, useJobPermits, useChangeOrders, useJobQuote, useToggleJobCheck, useSetEquipmentPulled, useJobInvoice, type Job, type JobVisit } from "./jobs";
import { InternalNotes } from "@/lib/internalNotes";
import { MaterialRequestsQueue } from "./MaterialRequestsQueue";
import { specLines, specGroups, specProgress, useJobEquipFacts, useInstallBank } from "./installSpec";
import {
  buildWorkOrderHtml, dayText, fmtDate, installDaysOf, loadWorkOrder, openWorkOrder, permitTag, sendToSub, subFor, workOrderEmail,
  type SendOutcome,
} from "./workOrder";
import { checkSubResend, subSendState } from "./resendPrompt";
import { RecordHistory } from "@/components/RecordHistory";
import { InfoTip } from "@/components/InfoTip";
import { CopyCode, CopyListButton } from "@/components/Copyable";
import { useJobAhri, ratingText, AHRI_TYPE_LABEL } from "./ahri";

// INSTALL-SPEC-02: the install-detail questions live in ./installSpec (shared
// with the crew app); the office card is ./InstallDetailsCard.

const fmtStamp = (iso: string) =>
  new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

// ── Subcontractor directory ─────────────────────────────────────────────────
export interface Subcontractor {
  id: string; name: string; trade: string; email: string | null;
  phone: string | null; notes: string | null; active: boolean;
}
export function useSubcontractors() {
  return useQuery({
    queryKey: ["subcontractors"],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<Subcontractor[]> => {
      const { data, error } = await supabase.from("pegasus_subcontractor")
        .select("id, name, trade, email, phone, notes, active").order("name");
      if (error) throw error;
      return (data ?? []) as Subcontractor[];
    },
  });
}
function useSaveSub() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: Partial<Subcontractor> & { name: string }) => {
      const { data: u } = await supabase.auth.getUser();
      if (v.id) {
        const { id, ...patch } = v;
        const { error } = await supabase.from("pegasus_subcontractor")
          .update({ ...patch, updated_at: new Date().toISOString() }).eq("id", id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("pegasus_subcontractor").insert({
          name: v.name, trade: v.trade ?? "Electrical", email: v.email ?? null,
          phone: v.phone ?? null, notes: v.notes ?? null, active: true,
          created_by: u?.user?.email ?? null,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["subcontractors"] }),
  });
}

// ── Send history ────────────────────────────────────────────────────────────
export interface JobSend {
  id: string; sub_id: string | null; to_email: string; to_name: string | null;
  /** "test" = a test job's work order: recorded, never sent (TEST-FLAG-01). */
  status: "held" | "sent" | "failed" | "test"; sent_by: string | null; sent_at: string;
  /** SUB-SEND-02: the install day + window the sub was told (null = sent before a date existed). */
  install_date: string | null; install_window: string | null;
  /** SUB-AUTO-RESEND: the work order, or a "postponed" notice. */
  kind: "work_order" | "postponed";
}
export function useJobSends(jobId: string) {
  return useQuery({
    queryKey: ["job_sends", jobId],
    queryFn: async (): Promise<JobSend[]> => {
      const { data, error } = await supabase.from("pegasus_job_send")
        .select("id, sub_id, to_email, to_name, status, sent_by, sent_at, install_date, install_window, kind")
        .eq("job_id", jobId).order("sent_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as JobSend[];
    },
  });
}

// The customer's phones/email for the scope header (the packet's contact block).
function useJobContact(custno: string | null) {
  return useQuery({
    queryKey: ["job_contact", custno],
    enabled: !!custno,
    queryFn: async () => {
      const { data } = await supabase.from("mirror_location")
        .select("phone1, phone2, email").eq("custno", custno!).order("locno").limit(1);
      const r = (data ?? [])[0] as { phone1?: string | null; phone2?: string | null; email?: string | null } | undefined;
      return { phone1: r?.phone1 ?? null, phone2: r?.phone2 ?? null, email: r?.email ?? null };
    },
  });
}

// ── The work order document + sender live in ./workOrder (one builder for the
// job page, the warehouse print and the "install moved" prompt).
export { jobAddressLine, installDaysOf, firstInstallDay } from "./workOrder";

// ── SUB-SEND-02 (Seth 2026-09-25: "how can we have page to powerhouse on that
// first tab when we haven't even entered a date yet … Contractor would need the
// date of install details so they know when to show up") — the send lives on
// Scheduled, opens only once the install visit has a date, lists what the sub
// will get, and flags a resend when the date or window moves after sending.
export function SubcontractorSendCard({ job, visits, onGo }: {
  job: Job;
  visits: JobVisit[];
  /** Jump to another stage's card (Schedule it / Finish the details). */
  onGo?: (stage: "workorder" | "scheduled", zone?: string) => void;
}) {
  const qc = useQueryClient();
  const memberName = useMemberName();
  const { data: equip } = useJobEquipment(job.id);
  const facts = useJobEquipFacts(equip);
  const { data: permitsRaw } = useJobPermits(job.id);
  // PERMIT-01: a cancelled permit is gone from the packet; requested / the
  // builder's own are labeled so the crew and sub read them right.
  const permits = (permitsRaw ?? []).filter((p) => p.status !== "cancelled");
  const { data: signals } = useCustomerSignals(job.custno);
  const { data: subs } = useSubcontractors();
  const { data: sends } = useJobSends(job.id);
  const [subId, setSubId] = useState<string>("");
  const [manageOpen, setManageOpen] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  // SUBS-02: "no subcontractor" is one flag shared with the Money tab's
  // invoice-matching card.
  const toggleCheck = useToggleJobCheck();
  const manualChecks: Record<string, boolean> = job.manual_checks ?? {};
  const noSubs = !!manualChecks["no_subs"];
  const toggleNoSubs = () => toggleCheck.mutate({ id: job.id, manual_checks: { ...manualChecks, no_subs: !noSubs } });

  const bank = useInstallBank();
  const activeSubs = (subs ?? []).filter((s) => s.active);
  const lastSend = (sends ?? [])[0] ?? null;
  // The sub who got the last one stays the pick; else the only / first active sub.
  const sub = activeSubs.find((s) => s.id === subId) ?? activeSubs.find((s) => s.id === lastSend?.sub_id) ?? activeSubs[0] ?? null;
  const days = installDaysOf(visits);
  const first = days[0] ?? null;
  const installerName = first?.empno ? (memberName(first.empno) ?? null) : null;
  const access = (signals ?? [])
    .filter((s) => s.status === "confirmed" && !s.archived_at && !!s.value)
    .map((s) => ({ label: s.signal_type.replace(/_/g, " "), value: s.value }));
  const equipment = (equip ?? []).map((e) => ({
    category: e.category, manufacturer: e.manufacturer, model: e.model ?? null,
    part_number: e.part_number, qty: e.qty || 1,
  }));
  const prog = specProgress(job.install_spec, facts, bank);
  // SUB-AUTO-RESEND: what the sub has vs what the job has now.
  const sendState = subSendState(lastSend, first);
  const subName = sub?.name ?? "The subcontractor";
  const lastName = lastSend?.to_name ?? subName;

  // Preview / Send build the SAME document the "install moved" prompt sends.
  // The preview is the email itself — an UPDATED / BACK ON THE SCHEDULE banner included.
  const build = async () => workOrderEmail(await loadWorkOrder(job.id, memberName, bank)).html;

  const send = useMutation({
    mutationFn: async (kind: "work_order" | "postponed"): Promise<SendOutcome> => {
      if (kind === "work_order" && !first) throw new Error("no_date");
      if (!sub?.email) throw new Error("no_email");
      const packet = await loadWorkOrder(job.id, memberName, bank);
      const target = subFor(packet, sub.id);
      if (!target) throw new Error("no_email");
      return sendToSub(packet, target, kind);
    },
    onSuccess: (status, kind) => {
      qc.invalidateQueries({ queryKey: ["job_sends", job.id] });
      void checkSubResend({ jobId: job.id });
      const what = kind === "postponed" ? "Postponed notice" : "Work order";
      setResult(status === "sent"
        ? { ok: true, text: `${what} sent to ${sub?.name}.` }
        : status === "test"
          ? { ok: false, text: "Test job — recorded, never sent." }
          : { ok: false, text: "Recorded as HELD — outbound messaging is in testing; it sends for real once comms go live." });
    },
    onError: (e: unknown) => {
      const m = (e as Error)?.message;
      setResult({ ok: false, text: m === "no_email" ? "This subcontractor has no email on file — add one via Manage." : m === "no_date" ? "Schedule the install first." : "Couldn't send — try again." });
    },
  });

  return (
    <section className="rounded-xl border border-hairline bg-white">
      <header className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-4">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900">
          <Send className="h-5 w-5 text-brand-500" /> Work Order to the Subcontractor
          <InfoTip label="What the subcontractor gets">The email is the work order: the install date, equipment, the installer guide, permits and access notes. Never pricing.</InfoTip>
        </h3>
        {noSubs && (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2.5 py-0.5 text-[13px] font-semibold text-slate-600">
            <Check className="h-3.5 w-3.5" /> None Needed
          </span>
        )}
        {!noSubs && lastSend && sendState === "current" && lastSend.kind !== "postponed" && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[13px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
            <Check className="h-3.5 w-3.5" strokeWidth={3} /> {lastSend.status === "held" ? "Sent (held — testing)" : lastSend.status === "test" ? "Recorded (test job)" : "Sent"}
          </span>
        )}
        <span className="ml-auto flex items-center gap-3">
          {!noSubs && (
            <button type="button" disabled={toggleCheck.isPending} onClick={toggleNoSubs}
              title="No electrician, plumber, or other sub on this job — closes the question here AND on Money & Close-out"
              className="rounded-full bg-slate-100 px-3.5 py-1.5 text-[14px] font-semibold text-slate-600 transition-colors hover:bg-slate-200">
              No Subcontractor Needed
            </button>
          )}
          <button type="button" onClick={() => setManageOpen(true)} className="inline-flex items-center gap-1.5 text-[14px] font-medium text-brand-700 transition-colors hover:text-brand-900">
            <Users className="h-4 w-4" /> Manage
          </button>
        </span>
      </header>

      <div className="space-y-4 px-5 py-4">
        {noSubs ? (
          <p className="flex flex-wrap items-center gap-2 text-[15px] text-slate-600">
            No subcontractor on this job.
            <button type="button" disabled={toggleCheck.isPending} onClick={toggleNoSubs}
              className="text-[14px] font-medium text-slate-500 underline-offset-2 transition-colors hover:text-slate-700 hover:underline">
              Undo
            </button>
          </p>
        ) : activeSubs.length === 0 ? (
          <p className="text-[15px] text-slate-600">No subcontractors yet — add one via Manage.</p>
        ) : (
          <>
            {/* What the sub gets — each piece, ready or not. */}
            <ul className="grid gap-x-8 gap-y-2.5 sm:grid-cols-2">
              <ReadyItem ok={!!first} required label={days.length > 1 ? "Install Days" : "Install Date"}
                value={first ? days.map((d) => `${fmtDate(d.date)}${d.window ? ` · ${d.window}` : ""}`).join("  +  ") : "Not scheduled yet"}
                action={!first && onGo ? { label: "Schedule it", run: () => onGo("scheduled", "zone-visits") } : undefined} />
              <ReadyItem ok={!!installerName} label="Installer" value={installerName ?? "Not assigned yet"}
                action={!installerName && onGo ? { label: "Assign", run: () => onGo("scheduled", "zone-visits") } : undefined} />
              <ReadyItem ok={equipment.length > 0} label="Equipment"
                value={equipment.length > 0 ? `${equipment.length} line${equipment.length === 1 ? "" : "s"}` : "None on the job yet"}
                action={equipment.length === 0 && onGo ? { label: "Add it", run: () => onGo("workorder", "zone-equipment-add") } : undefined} />
              <ReadyItem ok={prog.complete} label="Installer Guide"
                value={prog.total === 0 ? "Add equipment first" : prog.complete ? `All ${prog.total} answered` : `${prog.answered} of ${prog.total} answered`}
                action={!prog.complete && onGo ? { label: "Finish them", run: () => onGo("workorder", "zone-install-details") } : undefined} />
              <ReadyItem ok label="Permits"
                value={permits.length > 0 ? permits.map((p) => `${p.trade ?? "Permit"}${p.permit_no ? ` #${p.permit_no}` : ""}${permitTag(p)}`).join(" · ") : "None on this job"} />
              <ReadyItem ok label="Access & Site Notes" value={access.length > 0 ? `${access.length} confirmed note${access.length === 1 ? "" : "s"}` : "None on file"} />
            </ul>

            {job.is_test && (
              <p className="rounded-xl bg-rose-50 px-4 py-2.5 text-[14px] font-medium text-rose-800 ring-1 ring-inset ring-rose-200">
                Test job — sending records the work order here but never emails {subName}.
              </p>
            )}
            {sendState === "unscheduled" && lastSend?.install_date ? (
              <div className="flex flex-wrap items-start gap-3 rounded-xl bg-amber-50 px-4 py-3 ring-1 ring-inset ring-amber-200">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                <p className="min-w-0 flex-1 text-[15px] leading-relaxed text-amber-900">
                  <strong>{lastName} still has this install on {dayText({ date: lastSend.install_date, window: lastSend.install_window })}</strong>{" "}
                  — it isn&apos;t scheduled any more. Tell them it&apos;s postponed so nobody shows up; the new date goes out once it&apos;s set.
                </p>
                <Button variant="secondary" disabled={!sub?.email || send.isPending} onClick={() => send.mutate("postponed")}>
                  <Send className="h-4 w-4" /> {send.isPending ? "Sending…" : "Tell Them It's Postponed"}
                </Button>
              </div>
            ) : !first ? (
              <div className="flex items-start gap-3 rounded-xl bg-amber-50 px-4 py-3 ring-1 ring-inset ring-amber-200">
                <CalendarClock className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                <p className="text-[15px] leading-relaxed text-amber-900">
                  <strong>Schedule the install first.</strong> {subName} needs the date and arrival window to know when to
                  show up — sending opens as soon as the install visit has a date.
                </p>
              </div>
            ) : null}
            {(sendState === "moved" || sendState === "needs_date") && first && lastSend && (
              <div className="flex items-start gap-3 rounded-xl bg-amber-50 px-4 py-3 ring-1 ring-inset ring-amber-200">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                <p className="text-[15px] leading-relaxed text-amber-900">
                  {sendState === "needs_date"
                    ? <><strong>{lastName} was told the install is postponed.</strong> It&apos;s now {dayText(first)} — send them the work order with the new date.</>
                    : <><strong>The install moved since {lastName} got the work order.</strong>{" "}
                      {lastSend.install_date
                        ? <>They have {dayText({ date: lastSend.install_date, window: lastSend.install_window })}; it&apos;s now {dayText(first)}.</>
                        : <>They got it before the install had a date.</>}
                      {" "}Send it again so they show up on the right day — the email says what changed.</>}
                </p>
              </div>
            )}
            {first && !prog.complete && prog.total > 0 && (
              <p className="flex items-center gap-2 text-[14px] text-amber-800">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {prog.total - prog.answered} install detail{prog.total - prog.answered === 1 ? " is" : "s are"} still open — {subName} will see blanks. You can still send.
              </p>
            )}

            <div className="flex flex-wrap items-end gap-x-5 gap-y-3 border-t border-slate-100 pt-4">
              <label className="block">
                <span className="mb-1 block text-[14px] font-medium text-slate-500">Subcontractor</span>
                <select value={sub?.id ?? ""} onChange={(e) => setSubId(e.target.value)}
                  className="h-10 rounded-lg border border-hairline bg-white px-3 text-[15px] text-slate-800 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30">
                  {activeSubs.map((s) => <option key={s.id} value={s.id}>{s.name} · {s.trade}</option>)}
                </select>
              </label>
              <div className="min-w-0">
                <span className="mb-1 block text-[14px] font-medium text-slate-500">Sends to</span>
                {sub?.email ? (
                  <span className="block py-2 text-[15px] font-medium text-slate-700">{sub.email}</span>
                ) : (
                  <button type="button" onClick={() => setManageOpen(true)} className="inline-flex items-center gap-1 py-2 text-[15px] font-medium text-amber-700 hover:underline">
                    <AlertCircle className="h-4 w-4" /> No email on file — add it
                  </button>
                )}
              </div>
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <Button variant="secondary" onClick={() => openWorkOrder(build, false)} title="See exactly what the sub gets">
                  <Eye className="h-4 w-4" /> Preview
                </Button>
                <Button disabled={!first || !sub?.email || send.isPending} onClick={() => send.mutate("work_order")}
                  title={!first ? "Schedule the install first — the sub needs the date" : undefined}>
                  <Send className="h-4 w-4" /> {send.isPending ? "Sending…" : sendState === "moved" || sendState === "needs_date" ? "Send the New Date" : lastSend ? "Send Again" : "Send Work Order"}
                </Button>
              </div>
            </div>
            {result && (
              <p className={cn("text-[14px] font-medium", result.ok ? "text-emerald-700" : "text-amber-700")}>{result.text}</p>
            )}

            {/* Receipts */}
            {(sends ?? []).length > 0 && (
              <div className="border-t border-slate-100 pt-3">
                <div className="mb-1.5 text-[14px] font-semibold text-slate-600">Send History</div>
                <ul className="space-y-1.5">
                  {(sends ?? []).map((s) => (
                    <li key={s.id} className="flex flex-wrap items-center gap-2 text-[14px] text-slate-600">
                      <span className={cn("rounded-full px-2.5 py-0.5 text-[13px] font-semibold",
                        s.status === "sent" ? "bg-emerald-50 text-emerald-700" : s.status === "held" ? "bg-amber-50 text-amber-700" : s.status === "test" ? "bg-slate-100 text-slate-600" : "bg-rose-50 text-rose-700")}>
                        {s.status === "held" ? "Held (testing)" : s.status === "sent" ? "Sent" : s.status === "test" ? "Test Job — Not Sent" : "Failed"}
                      </span>
                      <span className="font-medium text-slate-800">{s.to_name ?? s.to_email}</span>
                      {s.kind === "postponed"
                        ? <span className="font-semibold text-amber-700">· Postponed notice</span>
                        : s.install_date && <span className="text-slate-600">· for {fmtDate(s.install_date)}{s.install_window ? ` · ${s.install_window}` : ""}</span>}
                      <span className="text-slate-500">· {fmtStamp(s.sent_at)}{s.sent_by ? ` · ${s.sent_by}` : ""}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>

      {manageOpen && <ManageSubsModal onClose={() => setManageOpen(false)} />}
    </section>
  );
}

/** One line of "what the sub gets": ready (check) or missing (amber), with a jump. */
function ReadyItem({ ok, label, value, action, required }: {
  ok: boolean; label: string; value: string; required?: boolean;
  action?: { label: string; run: () => void };
}) {
  return (
    <li className="flex min-w-0 items-start gap-2.5">
      {ok
        ? <Check className="mt-0.5 h-[18px] w-[18px] shrink-0 text-emerald-600" strokeWidth={3} />
        : <span className={cn("mt-1 h-3.5 w-3.5 shrink-0 rounded-full border-2", required ? "border-amber-500 bg-amber-100" : "border-slate-300")} />}
      <span className="min-w-0">
        <span className="block text-[14px] font-medium text-slate-500">{label}</span>
        <span className={cn("block text-[15px] font-semibold", ok ? "text-slate-800" : required ? "text-amber-800" : "text-slate-600")}>
          {value}
          {action && (
            <button type="button" onClick={action.run} className="ml-2 text-[14px] font-semibold text-brand-700 hover:underline">{action.label}</button>
          )}
        </span>
      </span>
    </li>
  );
}

// ── WAREHOUSE-01 (Seth 2026-08-25): "a full battle station for the warehouse
// ... exactly what is needed. What should be pulled. Any internal or external
// notes. A way for them to print the work order entry sheet ... very easy to
// read and very easy to not miss anything that was scoped to the job."
export function WarehouseTab({ job, visits }: { job: Job; visits: JobVisit[] }) {
  const memberName = useMemberName();
  const { data: equip } = useJobEquipment(job.id);
  const { data: permitsRaw } = useJobPermits(job.id);
  // PERMIT-01: a cancelled permit is gone from the packet; requested / the
  // builder's own are labeled so the crew and sub read them right.
  const permits = (permitsRaw ?? []).filter((p) => p.status !== "cancelled");
  const { data: signals } = useCustomerSignals(job.custno);
  const { data: contact } = useJobContact(job.custno);
  const setPulled = useSetEquipmentPulled();

  const installVisit = visits.find((v) => v.visit_role === "install") ?? visits[0] ?? null;
  const installerName = installVisit?.assigned_empno ? (memberName(installVisit.assigned_empno) ?? null) : null;
  const access = (signals ?? [])
    .filter((s) => s.status === "confirmed" && !s.archived_at && !!s.value)
    .map((s) => ({ label: s.signal_type.replace(/_/g, " "), value: s.value }));
  const list = equip ?? [];
  const pulledCount = list.filter((e) => !!e.pulled_at).length;
  const allPulled = list.length > 0 && pulledCount === list.length;
  const mainUnits = list.filter((e) => e.kind !== "accessory");
  const accessories = list.filter((e) => e.kind === "accessory");
  const facts = useJobEquipFacts(equip);
  const bank = useInstallBank();
  const specG = specGroups(specLines(job.install_spec, facts, bank), bank);
  const crewNotes = [installVisit?.summary, installVisit?.notes].filter(Boolean) as string[];

  // The printable work-order entry sheet — the same document the sub gets.
  const printSheet = () => openWorkOrder(async () => buildWorkOrderHtml(await loadWorkOrder(job.id, memberName, bank)), true);

  const PullRow = ({ e }: { e: (typeof list)[number] }) => {
    const isPulled = !!e.pulled_at;
    return (
      <li>
        <button type="button" disabled={setPulled.isPending}
          onClick={() => setPulled.mutate({ id: e.id, pulled: !isPulled })}
          title={isPulled ? "Pulled — click to undo" : "Mark pulled for this job"}
          className={cn(
            "flex w-full items-start gap-3.5 rounded-xl border px-4 py-3 text-left transition-colors",
            isPulled ? "border-emerald-200 bg-emerald-50/60" : "border-hairline bg-white hover:bg-slate-50",
          )}>
          <span className={cn(
            "mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2",
            isPulled ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-300 bg-white",
          )}>
            {isPulled && <Check className="h-4 w-4" strokeWidth={3} />}
          </span>
          <span className="min-w-0 flex-1">
            {/* COPY-01: the model / part # is what the warehouse retypes into a
                vendor portal or a label — click, tap, or hold copies it; the
                row's own click (mark pulled) is never triggered by the chip. */}
            <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[15px] font-bold text-slate-800">
              {e.manufacturer && <span>{e.manufacturer}</span>}
              {e.model || e.part_number ? (
                <CopyCode value={e.model || e.part_number} label={e.model ? "Copy model" : "Copy part number"} variant="text" size="lg" mono={false} className="font-bold" />
              ) : (
                <span>{e.category || "Item"}</span>
              )}
            </span>
            <span className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-slate-500">
              {e.category && <span>{e.category}</span>}
              {e.part_number && e.model && (
                <span className="inline-flex items-center gap-1">Part <CopyCode value={e.part_number} label="Copy part number" /></span>
              )}
              <span className={cn("font-bold tabular-nums", (e.qty || 1) > 1 ? "text-brand-700" : "text-slate-500")}>Qty {e.qty || 1}</span>
            </span>
            {e.notes && <span className="mt-1 block text-[13px] italic text-amber-700">{e.notes}</span>}
            {isPulled && (
              <span className="mt-0.5 block text-xs text-emerald-700">
                Pulled {fmtStamp(e.pulled_at!)}{e.pulled_by ? ` · ${e.pulled_by.split("@")[0]}` : ""}
              </span>
            )}
          </span>
          <span className={cn("shrink-0 text-sm font-bold", isPulled ? "text-emerald-700" : "text-amber-600")}>
            {isPulled ? "Pulled" : "To Pull"}
          </span>
        </button>
      </li>
    );
  };

  return (
    <div className="space-y-5">
      {/* THE BANNER — everything the warehouse needs at a glance. */}
      <section className="overflow-hidden rounded-xl border border-hairline bg-white">
        <div className="flex flex-wrap items-start justify-between gap-4 bg-brand-700 px-5 py-4 text-white">
          <div className="min-w-0">
            <p className="text-[13px] font-medium text-white/70">{job.customer_name ?? job.custno}{contact?.phone1 ? ` · ${contact.phone1}` : ""}</p>
            <h2 className="mt-0.5 flex items-center gap-2 text-2xl font-bold leading-tight">
              <MapPin className="h-5 w-5 shrink-0 text-white/70" />
              <span className="min-w-0">{job.address ?? job.title ?? "Install"}</span>
            </h2>
            <p className="mt-1.5 text-sm font-semibold text-white/90">
              {installVisit?.scheduled_date
                ? <>Install {fmtDate(installVisit.scheduled_date)}{installVisit.scheduled_window ? ` · ${installVisit.scheduled_window}` : ""}{installerName ? ` · ${installerName}` : ""}</>
                : "Install not scheduled yet"}
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-2">
            <Button variant="secondary" onClick={printSheet}>
              <Printer className="h-4 w-4" /> Print Work Order
            </Button>
            <span className={cn("rounded-full px-3 py-1 text-[13px] font-bold", allPulled ? "bg-emerald-500 text-white" : "bg-white/15 text-white")}>
              {list.length === 0 ? "Nothing scoped yet" : `${pulledCount} of ${list.length} pulled`}
            </span>
          </div>
        </div>
        {allPulled && (
          <div className="flex items-center gap-2 bg-emerald-50 px-5 py-2.5 text-sm font-semibold text-emerald-700">
            <Check className="h-4 w-4" strokeWidth={3} /> Everything pulled — this job is ready for the truck.
          </div>
        )}
      </section>

      {/* THE PULL SHEET — every unit and accessory scoped to the job. */}
      <section className="rounded-xl border border-hairline bg-white p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-base font-semibold text-slate-800">
            <Package className="h-[18px] w-[18px] text-brand-500" /> Pull Sheet
            <InfoTip label="How the pull sheet works">Tap a row to mark it pulled — the office sees the same list, stamped with who pulled it and when.</InfoTip>
          </h3>
          {/* COPY-01: every model / part # on the sheet as one list — paste
              into a vendor portal, an email, or a label printer. */}
          <CopyListButton
            items={list.map((e) => [e.manufacturer, e.model || e.part_number].filter(Boolean).join(" "))}
            noun="model numbers"
          />
        </div>
        {list.length === 0 ? (
          <p className="rounded-lg border border-dashed border-hairline py-6 text-center text-sm text-slate-400">
            Nothing scoped yet — the office adds equipment &amp; accessories under Work Order.
          </p>
        ) : (
          <div className="space-y-4">
            {mainUnits.length > 0 && (
              <div>
                <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">Equipment</div>
                <ul className="space-y-2">{mainUnits.map((e) => <PullRow key={e.id} e={e} />)}</ul>
              </div>
            )}
            {accessories.length > 0 && (
              <div>
                <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">Accessories</div>
                <ul className="space-y-2">{accessories.map((e) => <PullRow key={e.id} e={e} />)}</ul>
              </div>
            )}
          </div>
        )}
      </section>

      {/* MATERIAL-REQ-01: what the crew asked for on this job — pull it or order it. */}
      <MaterialRequestsQueue jobId={job.id} />

      {/* SCOPE OF WORK — what this install actually is. */}
      <section className="rounded-xl border border-hairline bg-white p-4">
        <h3 className="mb-3 flex items-center gap-2 text-base font-semibold text-slate-800">
          <Wrench className="h-[18px] w-[18px] text-brand-500" /> Scope of Work
        </h3>
        {job.title && <p className="mb-2 text-[15px] font-semibold text-slate-800">{job.title}</p>}
        {specG.length > 0 ? (
          <div className="space-y-3">
            {specG.map((g) => (
              <div key={g.title}>
                <div className="mb-1 text-[14px] font-bold text-slate-700">{g.title}</div>
                <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-[200px_minmax(0,1fr)]">
                  {g.lines.map((r) => (
                    <Fragment key={r.key}>
                      <dt className="text-[14px] text-slate-500">{r.label}</dt>
                      <dd className="text-[15px] font-semibold text-slate-800">{r.value}</dd>
                    </Fragment>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-500">Install details not filled in yet — the office completes them under Work Order.</p>
        )}
        {(permits ?? []).length > 0 && (
          <p className="mt-2.5 border-t border-slate-100 pt-2.5 text-sm text-slate-600">
            <span className="font-semibold text-slate-500">Permits:</span>{" "}
            {(permits ?? []).map((p) => `${p.trade ?? "Permit"}${p.permit_no ? ` #${p.permit_no}` : ""}${permitTag(p)}`).join(" · ")}
          </p>
        )}
        {access.length > 0 && (
          <div className="mt-2.5 border-t border-slate-100 pt-2.5">
            <div className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">
              <KeyRound className="h-3.5 w-3.5" /> Access &amp; Site Notes
            </div>
            <ul className="space-y-1">
              {access.map((a, i) => (
                <li key={i} className="text-sm text-slate-700"><span className="font-semibold capitalize text-slate-500">{a.label}:</span> {a.value}</li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* NOTES — the crew-facing words + the office's internal thread, so the
          warehouse misses nothing that was said about this job. */}
      {crewNotes.length > 0 && (
        <section className="rounded-xl border border-hairline bg-white p-4">
          <h3 className="mb-2 flex items-center gap-2 text-base font-semibold text-slate-800">
            <StickyNote className="h-[18px] w-[18px] text-brand-500" /> Visit Notes
          </h3>
          <ul className="space-y-1.5">
            {crewNotes.map((n, i) => <li key={i} className="text-sm leading-relaxed text-slate-700">{n}</li>)}
          </ul>
        </section>
      )}
      <InternalNotes
        entityType="job"
        entityId={job.id}
        title="Internal notes — office & warehouse"
        placeholder="Anything the warehouse should know while pulling this job — shortages, substitutions, staging spot. Never printed or sent to the customer."
      />
    </div>
  );
}

// ── Manage subcontractors ──────────────────────────────────────────────────
const TRADES = ["Electrical", "Plumbing", "Crane", "Asbestos", "Duct cleaning", "Other"];
function ManageSubsModal({ onClose }: { onClose: () => void }) {
  const { data: subs } = useSubcontractors();
  const save = useSaveSub();
  const [editId, setEditId] = useState<string | "new" | null>(null);
  const [f, setF] = useState({ name: "", trade: "Electrical", email: "", phone: "", notes: "" });

  const startEdit = (s: Subcontractor | null) => {
    setEditId(s ? s.id : "new");
    setF({ name: s?.name ?? "", trade: s?.trade ?? "Electrical", email: s?.email ?? "", phone: s?.phone ?? "", notes: s?.notes ?? "" });
  };
  const submit = () => {
    if (!f.name.trim()) return;
    save.mutate(
      { id: editId === "new" ? undefined : (editId as string), name: f.name.trim(), trade: f.trade, email: f.email.trim() || null, phone: f.phone.trim() || null, notes: f.notes.trim() || null },
      { onSuccess: () => setEditId(null) },
    );
  };
  const inputCls = "w-full rounded-md border border-hairline px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30";

  return (
    <Modal open onClose={onClose} title="Subcontractors" subtitle="Who can receive a work order — edit names, emails, add new trades" size="md">
      <div className="space-y-2">
        {(subs ?? []).map((s) => (
          <div key={s.id} className={cn("rounded-lg border p-3", s.active ? "border-hairline" : "border-dashed border-slate-200 opacity-60")}>
            {editId === s.id ? (
              <SubForm f={f} setF={setF} onCancel={() => setEditId(null)} onSave={submit} pending={save.isPending} inputCls={inputCls} />
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-800">{s.name} <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">{s.trade}</span></div>
                  <div className="mt-0.5 text-xs text-slate-500">{s.email || <span className="text-amber-600">no email</span>}{s.phone ? ` · ${s.phone}` : ""}</div>
                </div>
                <button type="button" onClick={() => startEdit(s)} title="Edit" className="rounded p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>
                <button
                  type="button"
                  onClick={() => save.mutate({ id: s.id, name: s.name, active: !s.active })}
                  title={s.active ? "Deactivate — kept for history, hidden from the send list" : "Reactivate"}
                  className="rounded p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                >
                  {s.active ? <X className="h-4 w-4" /> : <RotateCcw className="h-4 w-4" />}
                </button>
              </div>
            )}
          </div>
        ))}

        {editId === "new" ? (
          <div className="rounded-lg border border-brand-200 bg-brand-50/40 p-3">
            <SubForm f={f} setF={setF} onCancel={() => setEditId(null)} onSave={submit} pending={save.isPending} inputCls={inputCls} />
          </div>
        ) : (
          <button type="button" onClick={() => startEdit(null)} className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:border-brand-300 hover:text-brand-700">
            <Plus className="h-4 w-4" /> Add a subcontractor
          </button>
        )}
        <div className="flex justify-end border-t border-hairline pt-3">
          <Button variant="secondary" size="sm" onClick={onClose}>Done</Button>
        </div>
      </div>
    </Modal>
  );
}

function SubForm({ f, setF, onCancel, onSave, pending, inputCls }: {
  f: { name: string; trade: string; email: string; phone: string; notes: string };
  setF: (v: { name: string; trade: string; email: string; phone: string; notes: string }) => void;
  onCancel: () => void; onSave: () => void; pending: boolean; inputCls: string;
}) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Company name" className={inputCls} autoFocus />
        <select value={f.trade} onChange={(e) => setF({ ...f, trade: e.target.value })} className={inputCls}>
          {TRADES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email (work orders go here)" className={inputCls} />
        <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Phone" className={inputCls} />
      </div>
      <input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Notes (optional)" className={inputCls} />
      <div className="flex items-center justify-end gap-2">
        <button type="button" onClick={onCancel} className="text-xs font-medium text-slate-500 hover:text-slate-700">Cancel</button>
        <Button size="sm" disabled={!f.name.trim() || pending} onClick={onSave}>
          <Check className="h-3.5 w-3.5" /> {pending ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}


// ── SUMMARY (Seth 2026-08-18: "a summary that shows literally the entire
// scope of the job with any changes") — the LAST tab: the whole job on one
// read-only page. Sold facts, stage, equipment w/ serials, install details,
// schedule, permits, change orders, money, and every work-order send.
export function JobSummaryTab({ job, visits, costing }: {
  job: Job;
  visits: JobVisit[];
  costing: { quoted: number; actual: number; margin: number };
}) {
  const memberName = useMemberName();
  const { data: equip } = useJobEquipment(job.id);
  const { data: ahri } = useJobAhri(job.id);
  const { data: permitsRaw } = useJobPermits(job.id);
  // PERMIT-01: a cancelled permit is gone from the packet; requested / the
  // builder's own are labeled so the crew and sub read them right.
  const permits = (permitsRaw ?? []).filter((p) => p.status !== "cancelled");
  const { data: cos } = useChangeOrders(job.id);
  const { data: sends } = useJobSends(job.id);
  const { data: quote } = useJobQuote(job.id);
  const { data: inv } = useJobInvoice(job.id);
  const money = (n: number | null | undefined) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
  const facts = useJobEquipFacts(equip);
  const bank = useInstallBank();
  const specG = specGroups(specLines(job.install_spec, facts, bank), bank);

  const Sec = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <section className="rounded-xl border border-hairline bg-white p-4">
      <h3 className="mb-2.5 text-[13px] font-semibold uppercase tracking-wider text-slate-500">{title}</h3>
      {children}
    </section>
  );

  return (
    <div className="space-y-4">
      <Sec title="The sale">
        <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-slate-700">
          <span><span className="text-slate-400">Sold</span> <strong>{fmtDate(job.created_at)}</strong></span>
          {job.sale_tech && <span><span className="text-slate-400">by</span> <strong>{memberName(job.sale_tech) ?? job.sale_tech}</strong></span>}
          <span><span className="text-slate-400">Contract</span> <strong className="tabular-nums">{money(job.sell_price)}</strong></span>
          {quote && (
            <Link to={`/quotes/${quote.id}`} className="inline-flex items-center gap-1 font-medium text-brand-700 hover:underline">
              <FileText className="h-3.5 w-3.5" /> From accepted proposal{quote.accepted_at ? ` (${fmtDate(quote.accepted_at)})` : ""} <ArrowRight className="h-3 w-3" />
            </Link>
          )}
          {job.source_dispatch && (
            <Link to={`/dispatch/${job.source_dispatch}`} className="font-medium text-brand-700 hover:underline">From service call #{job.source_dispatch}</Link>
          )}
        </div>
        {/* JOB-SOLD-01: the load calc and the Equipment Check, as they stand. */}
        {(job.load_calc || job.equip_confirmed_at) && (
          <div className="mt-2 flex flex-wrap gap-x-8 gap-y-1 border-t border-slate-100 pt-2 text-sm text-slate-700">
            {job.load_calc?.not_needed ? (
              <span><span className="text-slate-400">Load calc</span> <strong>not needed</strong>{job.load_calc.reason ? ` — ${job.load_calc.reason}` : ""}</span>
            ) : job.equip_check?.load && (job.equip_check.load.heat || job.equip_check.load.cool) ? (
              <span><span className="text-slate-400">Load calc</span> <strong className="tabular-nums">{[
                job.equip_check.load.heat ? `${job.equip_check.load.heat.toLocaleString("en-US")} heat loss` : null,
                job.equip_check.load.cool ? `${job.equip_check.load.cool.toLocaleString("en-US")} heat gain` : null,
              ].filter(Boolean).join(" · ")}</strong> Btu/h</span>
            ) : null}
            {job.equip_confirmed_at && (
              <span><span className="text-slate-400">Equipment checked</span> <strong>{fmtDate(job.equip_confirmed_at)}</strong>
                {job.equip_confirmed_by ? ` by ${job.equip_confirmed_by.split("@")[0]}` : ""}
                {job.equip_confirm_note ? <span className="text-slate-500"> — “{job.equip_confirm_note}”</span> : null}</span>
            )}
          </div>
        )}
      </Sec>

      <Sec title="Equipment & accessories">
        {(equip ?? []).length === 0 ? (
          <p className="text-sm text-slate-400">Nothing on the job yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {(equip ?? []).map((e) => (
              <li key={e.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-1.5 text-sm">
                <span className="font-medium text-slate-800">{[e.manufacturer, e.model || e.part_number].filter(Boolean).join(" ")}</span>
                {e.category && <span className="text-xs text-slate-500">{e.category}</span>}
                {(e.qty || 1) > 1 && <span className="text-xs text-slate-400">×{e.qty}</span>}
                <span className="ml-auto text-xs tabular-nums text-slate-400">{e.serial ? `S/N ${e.serial}` : "no serial yet"}</span>
              </li>
            ))}
          </ul>
        )}
        {/* AHRI-01: the matched system's AHRI number(s) and ratings. */}
        {(ahri ?? []).map((a) => (
          <p key={a.id} className="mt-1.5 text-sm text-slate-700">
            <span className="font-semibold">AHRI {a.ahri_ref}</span>
            {a.system_type ? ` · ${AHRI_TYPE_LABEL[a.system_type]}` : ""}
            {ratingText(a) ? ` · ${ratingText(a)}` : ""}
          </p>
        ))}
      </Sec>

      {specG.length > 0 && (
        <Sec title="Installer guide">
          <div className="space-y-2.5">
            {specG.map((g) => (
              <div key={g.title}>
                <div className="mb-0.5 text-[14px] font-bold text-slate-700">{g.title}</div>
                <ul className="space-y-0.5 text-sm text-slate-700">
                  {g.lines.map((r) => (
                    <li key={r.key}><span className="font-medium text-slate-500">{r.label}:</span> {r.value}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Sec>
      )}

      <Sec title="Schedule">
        {visits.length === 0 ? (
          <p className="text-sm text-slate-400">No visits yet.</p>
        ) : (
          <ul className="space-y-1 text-sm text-slate-700">
            {visits.map((v) => (
              <li key={v.id} className="flex flex-wrap items-center gap-2">
                <CalendarClock className="h-3.5 w-3.5 text-slate-400" />
                <span className="font-medium capitalize">{v.visit_role ?? "visit"}</span>
                <span>{v.scheduled_date ? `${fmtDate(v.scheduled_date)}${v.scheduled_window ? ` · ${v.scheduled_window}` : ""}` : "not scheduled"}</span>
                {v.assigned_empno && <span className="text-slate-500">· {memberName(v.assigned_empno) ?? v.assigned_empno}</span>}
                <span className="ml-auto rounded-full bg-slate-100 px-2 py-0.5 text-[11px] capitalize text-slate-500">{v.status}</span>
              </li>
            ))}
          </ul>
        )}
      </Sec>

      <Sec title="Permits">
        {(permits ?? []).length === 0 ? (
          <p className="text-sm text-slate-400">None on this job.</p>
        ) : (
          <ul className="space-y-1 text-sm text-slate-700">
            {(permits ?? []).map((p) => (
              <li key={p.id}>{p.trade ?? "Permit"}{p.permit_no ? ` #${p.permit_no}` : ""}{p.jurisdiction ? ` · ${p.jurisdiction}` : ""}{p.fee != null && !p.by_builder ? ` · $${p.fee}` : ""}{permitTag(p)}</li>
            ))}
          </ul>
        )}
      </Sec>

      <Sec title="Changes">
        {(cos ?? []).length === 0 ? (
          <p className="text-sm text-slate-400">No change orders — the scope is as sold.</p>
        ) : (
          <ul className="space-y-1 text-sm text-slate-700">
            {(cos ?? []).map((c) => (
              <li key={c.id} className="flex flex-wrap items-center gap-2">
                <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold",
                  c.status === "approved" ? "bg-emerald-50 text-emerald-700" : c.status === "declined" ? "bg-rose-50 text-rose-600" : "bg-amber-50 text-amber-700")}>{c.status}</span>
                <span>{c.description}</span>
                <span className="ml-auto tabular-nums text-slate-600">{money(Number(c.amount || 0))}</span>
              </li>
            ))}
          </ul>
        )}
      </Sec>

      <Sec title="Money">
        <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
          <span className="text-slate-500">Contract <strong className="tabular-nums text-slate-800">{money(costing.quoted)}</strong></span>
          <span className="text-slate-500">Cost so far <strong className="tabular-nums text-slate-800">{money(costing.actual)}</strong></span>
          <span className="text-slate-500">Margin <strong className={cn("tabular-nums", costing.margin >= 0 ? "text-emerald-700" : "text-rose-600")}>{money(costing.margin)}</strong></span>
        </div>
      </Sec>

      <Sec title="Customer Invoice">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <span className={cn("rounded-full px-2.5 py-1 text-xs font-semibold",
            job.status === "closed" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700")}>
            {job.status === "closed" ? "Finalized" : "Draft — finalizes at close"}
          </span>
          <span className="text-slate-500">Total <strong className="tabular-nums text-slate-800">
            {money(job.status === "closed" && inv?.total != null ? Number(inv.total) : job.sell_price)}
          </strong></span>
          <button type="button" onClick={() => window.open(`/jobs/${job.id}/invoice`, "_blank")}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-hairline px-3 py-1.5 text-xs font-bold text-slate-600 transition-colors hover:bg-slate-50">
            <FileText className="h-3.5 w-3.5" /> View / Print Invoice
          </button>
        </div>
        <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
          Customer copy
          <InfoTip label="Where to edit the invoice">This is what the customer sees. Edit it under Money &amp; Close-out.</InfoTip>
        </p>
      </Sec>

      {(sends ?? []).length > 0 && (
        <Sec title="Work-order sends">
          <ul className="space-y-1 text-sm text-slate-700">
            {(sends ?? []).map((sd) => (
              <li key={sd.id} className="flex flex-wrap items-center gap-2">
                <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold",
                  sd.status === "sent" ? "bg-emerald-50 text-emerald-700" : sd.status === "held" ? "bg-amber-50 text-amber-700" : sd.status === "test" ? "bg-slate-100 text-slate-600" : "bg-rose-50 text-rose-600")}>
                  {sd.status === "held" ? "Held" : sd.status === "test" ? "Test — not sent" : sd.status}
                </span>
                <span className="font-medium">{sd.to_name ?? sd.to_email}</span>
                <span className="text-slate-400">· {fmtStamp(sd.sent_at)}{sd.sent_by ? ` · ${sd.sent_by}` : ""}</span>
              </li>
            ))}
          </ul>
        </Sec>
      )}

      {/* RECORD-HISTORY-01: who did what to THIS job — the "who moved this
          call" answer, on the record (admins/managers; audit RLS). */}
      <RecordHistory
        title="History — Who Changed What"
        targets={[
          { entity: "pegasus_job", id: job.id },
          ...visits.map((v) => ({ entity: "pegasus_dispatch", id: v.id })),
        ]}
      />
    </div>
  );
}


